import { createContext, useContext, useState, useCallback } from "react";
import type { ReactNode } from "react";
import { useAuth } from "./AuthContext";
import { WhatsAppLinkModal } from "@src/presentation/components/WhatsAppLinkModal";

interface WhatsAppLinkContextType {
    /**
     * Whether the WhatsApp link modal is currently open
     */
    isModalOpen: boolean;
    /**
     * Open the WhatsApp link modal
     * If user is already linked, opens WhatsApp directly instead
     */
    openLinkModal: () => void;
    /**
     * Close the WhatsApp link modal
     */
    closeLinkModal: () => void;
    /**
     * Open WhatsApp directly (bypasses modal)
     */
    openWhatsApp: () => void;
    /**
     * Smart action: opens modal if not linked, opens WhatsApp if linked
     */
    handleWhatsAppAction: () => void;
    /**
     * Whether the current user needs to link their WhatsApp
     */
    needsLinking: boolean;
    /**
     * Whether the current user has linked their WhatsApp
     */
    isLinked: boolean;
}

const WhatsAppLinkContext = createContext<WhatsAppLinkContextType | undefined>(undefined);

const WHATSAPP_URL = 'https://wa.me/233542480050';

export function WhatsAppLinkProvider({ children }: { children: ReactNode }) {
    const { isAuthenticated, user } = useAuth();
    const [isModalOpen, setIsModalOpen] = useState(false);

    const isLinked = Boolean(user?.whatsappLinked);
    const needsLinking = isAuthenticated && !isLinked;

    const openLinkModal = useCallback(() => {
        setIsModalOpen(true);
    }, []);

    const closeLinkModal = useCallback(() => {
        setIsModalOpen(false);
    }, []);

    const openWhatsApp = useCallback(() => {
        window.open(WHATSAPP_URL, '_blank', 'noopener,noreferrer');
    }, []);

    const handleWhatsAppAction = useCallback(() => {
        if (needsLinking) {
            openLinkModal();
        } else {
            openWhatsApp();
        }
    }, [needsLinking, openLinkModal, openWhatsApp]);

    return (
        <WhatsAppLinkContext.Provider
            value={{
                isModalOpen,
                openLinkModal,
                closeLinkModal,
                openWhatsApp,
                handleWhatsAppAction,
                needsLinking,
                isLinked,
            }}
        >
            {children}
            {/* Global WhatsApp Link Modal - rendered once at provider level */}
            <WhatsAppLinkModal isOpen={isModalOpen} onClose={closeLinkModal} />
        </WhatsAppLinkContext.Provider>
    );
}

export function useWhatsAppLink(): WhatsAppLinkContextType {
    const context = useContext(WhatsAppLinkContext);
    if (context === undefined) {
        throw new Error('useWhatsAppLink must be used within a WhatsAppLinkProvider');
    }
    return context;
}
